/* eslint-disable camelcase */
import useNativeSmt from '@swarm/core/hooks/data/useNativeSmt'
import useXGold from '@swarm/core/hooks/data/useXGold'
import useSx1155TokenIds from '@swarm/core/hooks/subgraph/sx1155/useSx1155TokenIds'
import useInvestSecurities from '@swarm/core/hooks/subgraph/useInvestSecurities'
import useXDotcAssets from '@swarm/core/hooks/subgraph/x-dotc/useXDotcAssets'
import { getCurrentConfig } from '@swarm/core/observables/configForNetwork'
import useTokensForOwner from '@swarm/core/services/alchemy/useTokensForOwner'
import NetworkFeature, {
  NetworkFeatureName,
} from '@swarm/core/services/networkFeatures'
import { mapBy } from '@swarm/core/shared/utils'
import { ZERO } from '@swarm/core/shared/utils/helpers'
import { isERC20Type } from '@swarm/core/shared/utils/tokens'
import { unifyAddressToId, useAccount } from '@swarm/core/web3'
import { TokenSelectorAsset } from '@swarm/types/tokens'
import { Sx1155AssetType } from '@swarm/types/x-subgraph/graphql'
import omit from 'lodash/omit'
import uniqBy from 'lodash/uniqBy'
import { useMemo } from 'react'

const { xGoldBundleAddress } = getCurrentConfig()

const useDefaultTokens = () => {
  const account = useAccount()
  const xGoldSupported = NetworkFeature.checkSupported(
    NetworkFeatureName.xGoldToken,
  )
  const { nativeSmt, loading: smtLoading } = useNativeSmt()

  const { securities, loading: securitiesLoading } = useInvestSecurities({
    includeBalances: true,
    account,
  })

  const { sx1155TokenIds: goldTokens } = useSx1155TokenIds({
    variables: {
      filter: {
        assetType: Sx1155AssetType.Gold,
      },
    },
  })

  const { tokens, loading } = useXDotcAssets({
    variables: {
      limit: 50,
      filter: {
        tradedVolume_gt: '0',
      },
    },
    pollInterval: undefined,
  })

  const includeOwnedTokens = !loading
    ? [
        ...tokens.filter(isERC20Type).map((token) => token.address),
        ...(xGoldSupported ? [xGoldBundleAddress] : []),
      ]
    : []

  // Fetch tokens with balances for the user
  const {
    tokens: ownedTokens,
    loading: ownedTokensLoading,
    tokensDictionary: ownedTokensDictionary,
  } = useTokensForOwner({
    account,
    initialFilter: { includeTokens: includeOwnedTokens },
    enabled: !loading,
  })

  const normalizedSecurities = useMemo<TokenSelectorAsset[]>(
    () =>
      securities.map((token) => ({
        ...omit(token, ['kyaInformation']),
        balance:
          ownedTokensDictionary.get(token.id)?.balance || token.balance || ZERO,
      })),
    [ownedTokensDictionary, securities],
  )

  const { token: xGold, loading: xGoldLoading } = useXGold()

  const normalizedXGold = useMemo<TokenSelectorAsset | null>(() => {
    if (!xGold) return null

    return {
      ...xGold,
      balance: ownedTokensDictionary.get(xGold.id)?.balance || ZERO,
    }
  }, [ownedTokensDictionary, xGold])

  const defaultTokens = useMemo<TokenSelectorAsset[]>(
    () =>
      uniqBy(
        [
          ...(nativeSmt ? [nativeSmt] : []), // SMT and Securities should override tokens above, which is why they are latest in the list.
          ...normalizedSecurities,
          ...(normalizedXGold ? [normalizedXGold] : []),
          ...ownedTokens,
          ...goldTokens,
        ],
        ({ id }) => unifyAddressToId(id),
      ),
    [normalizedSecurities, ownedTokens, nativeSmt, goldTokens, normalizedXGold],
  )

  const defaultTokensDictionary = mapBy(defaultTokens, 'id')

  return {
    tokens: defaultTokens,
    tokensDictionary: defaultTokensDictionary,
    loading:
      loading ||
      ownedTokensLoading ||
      smtLoading ||
      securitiesLoading ||
      xGoldLoading,
  }
}

export default useDefaultTokens
